import type { Article, ArticleCategory } from '../../types/article'
import { expiredStockIntro, expiredStockSections } from './expiredStock'

const IMG = 'https://cdn.magicpatterns.com/patterns/generated-images/'

export const articleCategories: ('All' | ArticleCategory)[] = [
  'All',
  'Running your business',
  'Sales & payments',
  'Stock & suppliers',
  'Money & finance',
  'Tax & compliance',
  'Kayko news',
]

/** Newest first. The first article is featured on the blog list. */
export const articles: Article[] = [
  {
    slug: 'stop-losing-money-on-expired-stock',
    category: 'Stock & suppliers',
    title: 'How to stop losing money on expired stock',
    subtitle: 'Five simple habits and a 15-minute weekly routine that keep products selling before their date.',
    excerpt: 'Expired milk, bread, and medicines quietly eat into your profit. Here’s how Rwandan shop owners are cutting waste with a few simple habits.',
    authorId: 'claudine',
    date: '18 September 2026',
    readMinutes: 6,
    image: `${IMG}03e8a36d-de0c-43b0-a298-585a33b4311a.jpg`,
    alt: 'Shopkeeper checking the expiry date on a packet in her Kigali mini-market',
    tags: ['Stock management', 'Retail', 'Reducing waste', 'Inventory'],
    product: 'inventory',
    intro: expiredStockIntro,
    sections: expiredStockSections,
  },
  {
    slug: 'guide-to-ebm-receipts',
    category: 'Tax & compliance',
    title: 'A simple guide to EBM receipts for small businesses',
    subtitle: 'What EBM is, who needs it, and how to issue receipts without slowing down your till.',
    excerpt: 'Every sale needs a proper receipt. We explain EBM in plain language and show how to stay compliant without extra paperwork.',
    authorId: 'eric',
    date: '11 September 2026',
    readMinutes: 5,
    image: `${IMG}daa4592c-3f4a-47f2-9514-50fa19b0e2fb.jpg`,
    alt: 'Customer receiving a receipt at a shop counter',
    tags: ['EBM', 'Tax', 'Receipts'],
    product: 'sales-pos',
  },
  {
    slug: 'get-customers-to-pay-what-they-owe',
    category: 'Sales & payments',
    title: '5 ways to get customers to pay what they owe',
    subtitle: 'Keep good relationships and still collect your money on time.',
    excerpt: 'Selling on credit keeps regulars loyal, but unpaid balances hurt your cash flow. Five polite, practical ways to collect.',
    authorId: 'claudine',
    date: '4 September 2026',
    readMinutes: 4,
    image: `${IMG}dab593c9-acf5-474b-8749-cea85e618096.jpg`,
    alt: 'Café owner speaking with a regular customer at the counter',
    tags: ['Customer credit', 'Cash flow'],
    product: 'customers',
  },
  {
    slug: 'is-your-shop-actually-profitable',
    category: 'Money & finance',
    title: 'How to know if your shop is actually profitable',
    subtitle: 'Busy doesn’t always mean profitable. Here’s how to check the numbers that matter.',
    excerpt: 'Sales can look strong while profit disappears. Learn the three numbers every owner should check each month.',
    authorId: 'eric',
    date: '28 August 2026',
    readMinutes: 7,
    image: `${IMG}bf8c75d0-c0c3-451c-b8e2-c025c368382a.jpg`,
    alt: 'Owner reviewing profit reports on a laptop',
    tags: ['Profit', 'Reports'],
    product: 'reports',
  },
  {
    slug: 'mobile-money-what-to-track',
    category: 'Sales & payments',
    title: 'Mobile money for business: what to track every day',
    subtitle: 'A daily checklist so every mobile money payment matches a sale.',
    excerpt: 'Most of your customers pay by phone. Here’s what to record each day so your cash-up always adds up.',
    authorId: 'claudine',
    date: '21 August 2026',
    readMinutes: 5,
    image: `${IMG}70fb5833-84ea-40d8-9b97-96476558b4c8.jpg`,
    alt: 'Customer paying by mobile phone at a small shop counter',
    tags: ['Mobile money', 'Cash-up'],
    product: 'sales-pos',
  },
  {
    slug: 'choosing-reliable-suppliers',
    category: 'Stock & suppliers',
    title: 'How to choose suppliers you can rely on',
    subtitle: 'Price matters, but so do delivery times, credit terms, and returns.',
    excerpt: 'The cheapest supplier isn’t always the best. What to compare before you commit to a distributor.',
    authorId: 'eric',
    date: '14 August 2026',
    readMinutes: 5,
    image: `${IMG}ab0101ab-8ad6-4588-83d1-46af0a029122.jpg`,
    alt: 'Checking delivered stock in a storeroom',
    tags: ['Suppliers', 'Purchasing'],
    product: 'purchases',
  },
  {
    slug: 'separate-business-and-personal-money',
    category: 'Money & finance',
    title: 'Why you should separate business and personal money',
    subtitle: 'The simplest step towards clear records and better access to finance.',
    excerpt: 'Taking cash from the drawer for home costs makes profit impossible to see. How to pay yourself properly instead.',
    authorId: 'claudine',
    date: '7 August 2026',
    readMinutes: 4,
    image: `${IMG}c428e88a-0bce-4050-a006-e40f44c2650d.jpg`,
    alt: 'Recording an expense with a receipt on a phone',
    tags: ['Bookkeeping', 'Cash flow'],
    product: 'expenses',
  },
  {
    slug: 'hiring-your-first-shop-assistant',
    category: 'Running your business',
    title: 'Hiring your first shop assistant: a practical checklist',
    subtitle: 'From the job description to their first week at the till.',
    excerpt: 'Ready to hire help? What to agree on, how to train, and how to keep control of sales and cash.',
    authorId: 'eric',
    date: '31 July 2026',
    readMinutes: 6,
    image: 'https://cdn.magicpatterns.com/uploads/hrSxrDybaWkqRaXzBR7V3y/ChatGPT_Image_Sep_26,_2026,_04_09_02_PM-1.png',
    alt: 'Shop assistant serving customers in a retail shop',
    tags: ['Staff', 'Retail'],
    product: 'sales-pos',
  },
  {
    slug: 'september-early-access',
    category: 'Kayko news',
    title: 'September Early Access: SMS stock alerts and branch reports',
    subtitle: 'What’s new in Kayko this month, and how to switch it on.',
    excerpt: 'Low-stock alerts by SMS, consolidated branch reports, and faster EBM printing are now in Early Access.',
    authorId: 'claudine',
    date: '24 July 2026',
    readMinutes: 3,
    image: `${IMG}ecd9d923-5c1a-43ac-a833-0b82a75cd9c0.jpg`,
    alt: 'Business owner reviewing branch reports',
    tags: ['Product updates'],
    product: 'inventory',
  },
  {
    slug: 'plan-for-slow-months',
    category: 'Running your business',
    title: 'How to plan for slow months',
    subtitle: 'Use last year’s numbers to prepare for quieter seasons.',
    excerpt: 'Every business has quiet months. Here’s how to spot them early and keep cash flowing.',
    authorId: 'eric',
    date: '17 July 2026',
    readMinutes: 5,
    image: `${IMG}a909e49b-7014-4cb7-9541-1c09b62af1c4.jpg`,
    alt: 'Café owner reviewing the week behind the counter',
    tags: ['Planning', 'Cash flow'],
    product: 'reports',
  },
  {
    slug: 'records-lenders-look-for',
    category: 'Money & finance',
    title: 'What records lenders look for in a small business',
    subtitle: 'How consistent sales and expense records support a financing application.',
    excerpt: 'Thinking about working capital? These are the records that help show how your business really performs.',
    authorId: 'claudine',
    date: '10 July 2026',
    readMinutes: 6,
    image: `${IMG}d825f78d-1c2e-4866-ad21-178ec126a998.jpg`,
    alt: 'Business owner meeting a lending adviser',
    tags: ['Business financing', 'Records'],
    product: 'business-finance',
  },
  {
    slug: 'pricing-products-for-profit',
    category: 'Running your business',
    title: 'How to price your products for profit',
    subtitle: 'A simple formula for setting prices that cover every cost.',
    excerpt: 'Copying your neighbour’s prices can leave you losing money. A step-by-step way to price with confidence.',
    authorId: 'eric',
    date: '3 July 2026',
    readMinutes: 5,
    image: 'https://cdn.magicpatterns.com/uploads/c8Nf8XJGRxSAWm6B5i5fj7/ChatGPT_Image_Sep_26,_2026,_04_09_09_PM-1.png',
    alt: 'Workshop owner calculating costs for an order',
    tags: ['Pricing', 'Profit'],
    product: 'reports',
  },
  {
    slug: 'vat-basics-for-small-businesses',
    category: 'Tax & compliance',
    title: 'VAT basics for small businesses in Rwanda',
    subtitle: 'When VAT applies, and how to keep records ready for filing.',
    excerpt: 'A plain-language overview of VAT registration and the records to keep. Always confirm details with RRA or your accountant.',
    authorId: 'eric',
    date: '26 June 2026',
    readMinutes: 6,
    image: 'https://cdn.magicpatterns.com/uploads/iDUc4UaB1F5bDm8tDia9dE/ChatGPT_Image_Sep_26,_2026,_04_09_04_PM-3.png',
    alt: 'Pharmacy counter with receipts and records',
    tags: ['Tax', 'VAT'],
    product: 'reports',
  },
]

export function getArticle(slug: string): Article | undefined {
  return articles.find((a) => a.slug === slug)
}
